import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Home, BookOpen, Users, GraduationCap, ArrowLeft, ArrowRight,
  Check, Loader2, Heart, Cross, Sparkles, Shield
} from 'lucide-react';
import PasswordStrengthMeter, { isPasswordValid } from '@/components/PasswordStrengthMeter';

const HomeschoolRegister = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    family_name: '',
    children_count: 1,
    grade_levels: [],
    curriculum_preferences: [],
    enable_bible_curriculum: false
  });

  const gradeOptions = [
    { value: 'pre-k', label: 'Pre-K (3-4 years)' },
    { value: 'kindergarten', label: 'Kindergarten (5-6 years)' },
    { value: '1st', label: '1st Grade' },
    { value: '2nd', label: '2nd Grade' },
    { value: '3rd', label: '3rd Grade' },
    { value: '4th', label: '4th Grade' },
    { value: '5th', label: '5th Grade' },
    { value: '6th', label: '6th Grade' },
    { value: '7th', label: '7th Grade' },
    { value: '8th', label: '8th Grade' },
  ];

  const curriculumOptions = [
    { value: 'el_education', label: 'EL Education (ELA)', description: 'Research-based literacy curriculum',
      details: 'A K-8 English Language Arts curriculum from EL Education. Uses close-reading of complex texts, structured writing, and Socratic discussions. Great fit for families that want a rigorous, standards-aligned literacy path.' },
    { value: 'envision_math', label: 'Envision Math', description: 'Visual and conceptual math approach',
      details: 'Pearson\'s Envision Math builds each concept through visual models before symbolic notation. Strong on step-by-step problem solving and mental math. Best for learners who need concrete visual anchors before abstract algorithms.' },
    { value: 'im_math', label: 'Illustrative Mathematics (IM)', description: 'Problem-based math learning',
      details: 'IM K-12 opens every lesson with a real problem the child has to reason through before any formula is given. Strong on discussion, multiple solution paths, and building conceptual understanding. Great for kids who love a challenge.' },
    { value: 'science', label: 'Science Explorers', description: 'Hands-on science activities',
      details: 'Weekly inquiry-based science projects covering life, earth, and physical science. Every lesson pairs a short "big idea" video with a hands-on activity your child can do at the kitchen table with common household items.' },
    { value: 'cpa', label: 'Concrete-Pictorial-Abstract (CPA)', description: 'Singapore-style: manipulate objects → draw → symbols',
      details: 'A three-stage approach (also called Singapore Math\'s "bar model" style). Children first manipulate physical objects (concrete), then draw pictures/bar diagrams (pictorial), then move to numerical symbols (abstract). Especially powerful for word problems, fractions, and early algebra — children build deep understanding before they memorize.' },
    { value: 'independence', label: 'Independence-Based Learning', description: 'Self-directed goal setting, choice boards, agency',
      details: 'Learners pick from a menu of daily activities ("choice boards") and set their own weekly goals. Grown-ups act as coaches. Builds executive function, self-regulation, and intrinsic motivation. Recommended for ages 6+ once children can read directions on their own.' },
    { value: 'reggio_emilia', label: 'Reggio Emilia', description: 'Child-led, project-based, image-of-the-child philosophy',
      details: 'Originated in post-war Italy. Views the child as capable and curious ("image of the child"). Learning happens through long-form projects that follow the child\'s own questions. Art, movement, and nature are treated as core "languages" alongside words and numbers. Beautiful fit for preschoolers and early primary.' },
    { value: 'eyfs', label: 'EYFS (Early Years Foundation Stage)', description: 'UK statutory framework for ages 0-5',
      details: 'The UK\'s official framework for children ages 0-5, covering seven areas: communication & language, physical development, personal/social/emotional, literacy, math, understanding the world, and expressive arts & design. Playful, observation-led, and used by every state-funded nursery in England.' },
  ];

  const [expandedCurriculum, setExpandedCurriculum] = useState(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleGradeLevel = (grade) => {
    setFormData(prev => ({
      ...prev,
      grade_levels: prev.grade_levels.includes(grade)
        ? prev.grade_levels.filter(g => g !== grade)
        : [...prev.grade_levels, grade]
    }));
  };

  const toggleCurriculum = (curriculum) => {
    setFormData(prev => ({
      ...prev,
      curriculum_preferences: prev.curriculum_preferences.includes(curriculum)
        ? prev.curriculum_preferences.filter(c => c !== curriculum)
        : [...prev.curriculum_preferences, curriculum]
    }));
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        if (!formData.name || !formData.email || !formData.password) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          return false;
        }
        if (!isPasswordValid(formData.password)) {
          toast.error('Password does not meet complexity requirements. Please satisfy every requirement below the field.');
          return false;
        }
        return true;
      case 2:
        if (!formData.family_name) {
          toast.error('Please enter your family/homeschool name');
          return false;
        }
        return true;
      case 3:
        return true; // Grade levels optional
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/auth/register-homeschool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          family_name: formData.family_name,
          children_count: parseInt(formData.children_count) || 1,
          grade_levels: formData.grade_levels,
          curriculum_preferences: formData.curriculum_preferences,
          enable_bible_curriculum: formData.enable_bible_curriculum
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        // Handle Pydantic validation errors
        let errorMessage = 'Registration failed';
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            errorMessage = data.detail.map(e => e.msg || e).join(', ');
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          }
        }
        throw new Error(errorMessage);
      }

      // Log in the user
      localStorage.setItem('auth_token', data.access_token);
      login(data.access_token, data.user);
      
      toast.success('Welcome to Zazu! Your homeschool account is ready!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="p-4 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Home</span>
        </Link>
        <div className="flex items-center gap-2">
          <Home className="w-6 h-6 text-emerald-600" />
          <span className="font-bold text-emerald-700">Homeschool Registration</span>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                s < step ? 'bg-emerald-500 text-white' :
                s === step ? 'bg-emerald-600 text-white' :
                'bg-slate-200 text-slate-500'
              }`}>
                {s < step ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 4 && (
                <div className={`w-16 h-1 ${s < step ? 'bg-emerald-500' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Account Info */}
        {step === 1 && (
          <Card className="border-0 shadow-xl" data-testid="homeschool-step-1">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl">Create Your Account</CardTitle>
              <CardDescription>Let's start with your basic information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Your Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  data-testid="homeschool-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  data-testid="homeschool-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Create a strong password"
                  data-testid="homeschool-password-input"
                />
                <div className="mt-2">
                  <PasswordStrengthMeter password={formData.password} />
                </div>
              </div>
              <div>
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm your password"
                  data-testid="homeschool-confirm-password-input"
                />
              </div>
              <Button 
                onClick={nextStep} 
                className="w-full bg-teal-600 hover:bg-teal-700"
                data-testid="homeschool-next-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Homeschool Info */}
        {step === 2 && (
          <Card className="border-0 shadow-xl" data-testid="homeschool-step-2">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Home className="w-8 h-8 text-amber-600" />
              </div>
              <CardTitle className="text-2xl">Your Homeschool</CardTitle>
              <CardDescription>Tell us about your homeschool family</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="family_name">Family/Homeschool Name *</Label>
                <Input
                  id="family_name"
                  name="family_name"
                  value={formData.family_name}
                  onChange={handleInputChange}
                  placeholder="e.g., The Johnson Family"
                  data-testid="homeschool-family-name-input"
                />
              </div>
              <div>
                <Label htmlFor="children_count">Number of Children</Label>
                <Select 
                  value={String(formData.children_count)} 
                  onValueChange={(val) => setFormData({ ...formData, children_count: parseInt(val) })}
                >
                  <SelectTrigger data-testid="homeschool-children-count-select">
                    <SelectValue placeholder="Select number of children" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <SelectItem key={num} value={String(num)}>{num} {num === 1 ? 'child' : 'children'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={prevStep} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={nextStep} 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="homeschool-next-btn-2"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Grade Levels & Curriculum */}
        {step === 3 && (
          <Card className="border-0 shadow-xl" data-testid="homeschool-step-3">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl">Grade Levels & Curriculum</CardTitle>
              <CardDescription>Select what applies to your children (optional)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Grade Levels */}
              <div>
                <Label className="text-base font-semibold mb-3 block">Grade Levels</Label>
                <div className="grid grid-cols-2 gap-2">
                  {gradeOptions.map(grade => (
                    <button
                      key={grade.value}
                      onClick={() => toggleGradeLevel(grade.value)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        formData.grade_levels.includes(grade.value)
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`grade-${grade.value}`}
                    >
                      <span className="text-sm font-medium">{grade.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Curriculum Preferences */}
              <div>
                <Label className="text-base font-semibold mb-3 block">Curriculum Interests</Label>
                <div className="space-y-2">
                  {curriculumOptions.map(curr => {
                    const isSelected = formData.curriculum_preferences.includes(curr.value);
                    const isExpanded = expandedCurriculum === curr.value;
                    return (
                      <div
                        key={curr.value}
                        className={`w-full rounded-lg border-2 transition-all overflow-hidden ${
                          isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`curriculum-${curr.value}`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleCurriculum(curr.value)}
                          className="w-full p-4 text-left"
                          data-testid={`curriculum-select-${curr.value}`}
                        >
                          <div className="font-medium">{curr.label}</div>
                          <div className="text-sm text-slate-500">{curr.description}</div>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setExpandedCurriculum(isExpanded ? null : curr.value); }}
                          className="w-full text-left px-4 pb-3 -mt-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                          data-testid={`curriculum-learn-more-${curr.value}`}
                        >
                          {isExpanded ? '▲ Hide details' : '▼ Learn more about this approach'}
                        </button>
                        {isExpanded && (
                          <div className="px-4 pb-4 -mt-1 text-sm text-slate-700 bg-white/50 border-t border-slate-200" data-testid={`curriculum-details-${curr.value}`}>
                            <p className="pt-3 leading-relaxed">{curr.details}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={prevStep} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={nextStep} 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="homeschool-next-btn-3"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Bible Curriculum */}
        {step === 4 && (
          <Card className="border-0 shadow-xl" data-testid="homeschool-step-4">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-purple-600" />
              </div>
              <CardTitle className="text-2xl">Faith-Based Learning</CardTitle>
              <CardDescription>Would you like to include Christian content?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Bible Curriculum Option */}
              <div 
                onClick={() => setFormData({ ...formData, enable_bible_curriculum: !formData.enable_bible_curriculum })}
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.enable_bible_curriculum
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid="bible-curriculum-toggle"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    formData.enable_bible_curriculum ? 'bg-purple-500 text-white' : 'bg-slate-100'
                  }`}>
                    <Heart className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">Enable Bible Curriculum</h3>
                    <p className="text-slate-600 text-sm">
                      Include age-appropriate Bible stories, character lessons, and Scripture memory 
                      activities integrated into your child's learning journey.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Bible Stories</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Character Values</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Scripture Memory</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Prayer Activities</span>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    formData.enable_bible_curriculum 
                      ? 'border-purple-500 bg-purple-500' 
                      : 'border-slate-300'
                  }`}>
                    {formData.enable_bible_curriculum && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>
              </div>

              <p className="text-sm text-slate-500 text-center">
                You can change this setting anytime in your account preferences.
              </p>

              <div className="flex gap-3">
                <Button variant="outline" onClick={prevStep} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="homeschool-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Complete Registration
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Benefits Card */}
        <Card className="mt-6 border-0 shadow-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
          <CardContent className="p-6">
            <h3 className="font-bold text-lg mb-3">Homeschool Benefits Include:</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>Up to 5 student profiles included</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>AI-powered adaptive learning for each child</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>Progress reports and learning analytics</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>Multiple curriculum options (EL, Envision, IM)</span>
              </li>
              <li className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                <span>Safe, ad-free learning environment</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Already have account link */}
        <p className="text-center mt-6 text-slate-600">
          Already have an account?{' '}
          <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-medium">
            Sign in here
          </Link>
        </p>
      </div>
    </div>
  );
};

export default HomeschoolRegister;
